import { useCallback, useMemo } from 'react';
import { useIoContext } from "../../../../../contexts";
import { useServiceBillingDetails } from "../../../../../hooks/reactQuery/queries/useServiceBillingDetails";
import { isAfterOrEqual, startOfDay } from "../../../../../infra/utils/dates-fns";
import { serviceModel } from "../../../../../models";
import { pluralize } from "../../../../../utils/pluralize";
import { InlineStartBillingMenuItem } from "../InlineStartBillingControl";
export const useCallouts = _ref => {
  let {
    testIds,
    service,
    relationshipEffectiveDate,
    selectedItem,
    isAmendedService,
    disabled = false
  } = _ref;
  const {
    InlineStartBillingControlTestIds
  } = testIds;
  const {
    getNow
  } = useIoContext();
  const getToday = useCallback(() => startOfDay(getNow()), [getNow]);
  const isProrated = serviceModel.isProrated(service);
  const isBilledUpfront = serviceModel.isBilledUpfront(service);
  const startBillingAt = serviceModel.startBillingAt(service);
  const {
    nextBillingDate,
    periods
  } = useServiceBillingDetails({
    service,
    relationshipEffectiveDate,
    enabled: !disabled
  });
  const shouldShowProrateWarning = useMemo(() => {
    if (!isProrated && isBilledUpfront && startBillingAt && nextBillingDate) {
      return nextBillingDate.getTime() !== startBillingAt.getTime();
    }
    return false;
  }, [nextBillingDate, startBillingAt, isProrated, isBilledUpfront]);
  return useMemo(() => {
    const callouts = [];
    if (disabled || selectedItem !== InlineStartBillingMenuItem.SpecificDate || !startBillingAt) {
      return callouts;
    }
    if (!isAmendedService && isAfterOrEqual(startBillingAt, getToday())) {
      if (relationshipEffectiveDate) {
        callouts.push({
          dataTestId: InlineStartBillingControlTestIds.Callouts.FutureDate(),
          text: "If the proposal is signed after the billing start date, charges for prior periods will be invoiced upon approval."
        });
      } else {
        callouts.push({
          dataTestId: InlineStartBillingControlTestIds.Callouts.OnAcceptanceDate(),
          text: "Because the agreement takes effect on acceptance, signing after the billing start date will begin billing in the next cycle and won't include prior charges."
        });
      }
    }
    if (shouldShowProrateWarning && !disabled) {
      callouts.push({
        dataTestId: InlineStartBillingControlTestIds.Callouts.Proration(),
        text: "As the start date doesn't align with the billing day, an additional full invoice will be issued. To avoid this, change the date or prorate the first payment."
      });
    }
    if (periods && periods > 0) {
      callouts.push({
        dataTestId: InlineStartBillingControlTestIds.Callouts.PriorPeriods(),
        text: "Invoices for ".concat(periods, " prior ").concat(pluralize('period', periods), " will be issued once the proposal is approved. If the proposal is signed after the next scheduled billing date, additional invoices for earlier periods will also be issued.")
      });
    }
    return callouts;
  }, [selectedItem, startBillingAt, shouldShowProrateWarning, periods, relationshipEffectiveDate, isAmendedService, disabled, getToday, InlineStartBillingControlTestIds]);
};